import { Info } from 'lucide-react'
import { type ComponentType, type ReactNode, useState } from 'react'
import { useViewportBand } from '../../lib/viewport'
import { DisplayRow } from '../DisplayRow'
import { DocSlot } from '../DocSlot'
import { FactRow } from '../FactRow'
import { PageActionFor, useDocSlot } from '../PageAction'
import './doc-facts.css'

type Fact = {
  label: string
  value: ReactNode
  icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>
}

/**
 * A document's own facts — its paper, what it draws on, when it was edited.
 *
 * Where the document IS the page, the facts are asked for rather than shown: a
 * band of them above the document is chrome between the reader and the thing
 * they opened. A View action reveals them as one slim line, pinned above the
 * document, and the header's own way out puts them away. Anywhere else they
 * stand as a band, read before the preview beneath it.
 */
export function DocFacts({
  facts,
  onDemand,
}: {
  facts: readonly Fact[]
  onDemand: boolean
}) {
  const [open, setOpen] = useState(false)
  const slot = useDocSlot()
  /* Beside the rails the header's own way out puts the facts away; in a hand
     that way out is inside a sheet, so the entry carries its close. */
  const handheld = useViewportBand() === 'handheld'
  if (facts.length === 0) return null
  if (!onDemand) return <FactRow values="text" facts={facts} />
  const line = (
    <dl className="doc-facts">
      {facts.map((fact) => (
        <div key={fact.label} className="doc-facts__fact">
          <dt className="doc-facts__label">
            {fact.icon ? <fact.icon size={13} aria-hidden /> : null}
            {fact.label}
          </dt>
          <dd className="doc-facts__value">{fact.value}</dd>
        </div>
      ))}
    </dl>
  )
  return (
    <>
      <PageActionFor
        type="view"
        label="Details"
        icon={Info}
        open={open}
        onSelect={() => setOpen((was) => !was)}
      />
      {/* Above a document that owns the screen the facts are one entry in the
        slot over it; elsewhere they open as a row above the preview. */}
      {slot ? (
        open ? (
          <DocSlot
            kind="details"
            size="tall"
            onClose={handheld ? () => setOpen(false) : undefined}
            closeLabel="Close details"
          >
            {line}
          </DocSlot>
        ) : null
      ) : (
        <DisplayRow open={open}>{line}</DisplayRow>
      )}
    </>
  )
}
